import type { UploadPhase, UploadTask } from '@/features/uploads/types/uploads';
import { captureEvent } from '@/shared/analytics/posthog';
import type { LocalUploadRetryEvent } from '../lib/uploadRetry';

type UploadFailureStage = UploadPhase | 'resumable_begin' | 'chunk_put' | 'complete';

interface ErrorLike {
  name?: unknown;
  message?: unknown;
  cause?: unknown;
  status?: unknown;
  transport?: unknown;
  operation?: unknown;
  xhrStatus?: unknown;
  xhrReadyState?: unknown;
  browserOnline?: unknown;
  chunkStartBytes?: unknown;
  chunkSizeBytes?: unknown;
  finalChunk?: unknown;
}

function localUploadProperties(item: UploadTask): Record<string, unknown> {
  return {
    transfer_id: item.id,
    session_id: item.backendId,
    organization_id: item.target.organizationId,
    project_id: item.target.projectId,
    media_type: item.mediaType ?? 'video',
    file_size_bytes: item.totalBytes,
    uploaded_bytes: item.bytes,
    auto_retry_attempts: item.autoRetryCount,
  };
}

interface RestartAnalyticsUpload {
  mediaType?: 'video' | 'mcap';
  bytes: number;
  totalBytes: number;
}

function restartProgressBucket(item: RestartAnalyticsUpload): string {
  if (item.totalBytes <= 0 || item.bytes <= 0) return '0';
  const percent = (item.bytes / item.totalBytes) * 100;
  if (percent < 25) return '1-24';
  if (percent < 50) return '25-49';
  if (percent < 75) return '50-74';
  if (percent < 100) return '75-99';
  return '100';
}

/** Restart telemetry deliberately excludes transfer/session/project identifiers, file names, hashes,
 * byte counts, and resumable URIs. These are the only properties restart events may reuse. */
function restartProperties(item: RestartAnalyticsUpload): Record<string, unknown> {
  return {
    media_type: item.mediaType ?? 'video',
    progress_bucket: restartProgressBucket(item),
  };
}

function restartErrorCategory(error: unknown): string {
  if (error instanceof Error && error.message.startsWith('restart_file_')) return 'invalid_file';
  const status = firstProperty(errorChain(error), 'status', 'number');
  if (status === 401) return 'authentication';
  if (status === 403 || status === 404) return 'authorization';
  if (status === 409) return 'conflict';
  if (status === 503) return 'storage_unavailable';
  if (error instanceof TypeError) return 'network';
  return 'unknown';
}

function errorChain(error: unknown): ErrorLike[] {
  const chain: ErrorLike[] = [];
  const seen = new Set<unknown>();
  let current = error;
  while (current && typeof current === 'object' && !seen.has(current) && chain.length < 5) {
    seen.add(current);
    const errorLike = current as ErrorLike;
    chain.push(errorLike);
    current = errorLike.cause;
  }
  return chain;
}

function diagnosticCode(chain: ErrorLike[]): string {
  for (const error of chain) {
    if (typeof error.message !== 'string') continue;
    const message = error.message.trim().toLowerCase();
    const normalized = message === 'network error' ? 'network_error' : message;
    if (/^[a-z][a-z0-9_]{0,63}$/.test(normalized)) return normalized;
  }
  return 'upload_failed';
}

function firstProperty(
  chain: ErrorLike[],
  property: keyof ErrorLike,
  expectedType: 'boolean' | 'number' | 'string',
): unknown {
  for (const error of chain) {
    const value = error[property];
    if (expectedType === 'boolean' && typeof value === 'boolean') return value;
    if (expectedType === 'number' && typeof value === 'number') return value;
    if (expectedType === 'string' && typeof value === 'string') return value;
  }
  return undefined;
}

/**
 * Builds the deliberately bounded upload diagnostic payload. Raw error messages, file names,
 * hashes, and signed/resumable URLs are excluded so operational telemetry cannot leak asset data.
 */
export function localUploadFailureProperties(
  item: UploadTask,
  stage: UploadFailureStage,
  error: unknown,
): Record<string, unknown> {
  const chain = errorChain(error);
  const errorName = firstProperty(chain, 'name', 'string');
  const status = firstProperty(chain, 'status', 'number');
  const progressPercent =
    item.totalBytes > 0 ? Math.round((item.bytes / item.totalBytes) * 10_000) / 100 : undefined;

  return {
    ...localUploadProperties(item),
    stage,
    error_code: diagnosticCode(chain),
    error_name: errorName,
    http_status: status,
    transport: firstProperty(chain, 'transport', 'string'),
    operation: firstProperty(chain, 'operation', 'string'),
    xhr_status: firstProperty(chain, 'xhrStatus', 'number'),
    xhr_ready_state: firstProperty(chain, 'xhrReadyState', 'number'),
    browser_online: firstProperty(chain, 'browserOnline', 'boolean'),
    chunk_start_bytes: firstProperty(chain, 'chunkStartBytes', 'number'),
    chunk_size_bytes: firstProperty(chain, 'chunkSizeBytes', 'number'),
    final_chunk: firstProperty(chain, 'finalChunk', 'boolean'),
    progress_percent: progressPercent,
  };
}

export function captureLocalUploadStarted(item: UploadTask): void {
  captureEvent('local_upload_started', localUploadProperties(item));
}

export function captureLocalUploadRetryRequested(item: UploadTask): void {
  captureEvent('local_upload_retry_requested', {
    ...localUploadProperties(item),
    failed_stage: item.phase,
    error_code: diagnosticCode([{ message: item.reason }]),
  });
}

export function captureLocalUploadReattachStarted(item: UploadTask): void {
  captureEvent('local_upload_reattach_started', localUploadProperties(item));
}

/** Emit only engine-selected milestones (25/50/75/100), never worker-chunk telemetry. */
export function captureLocalUploadReattachProgress(
  item: UploadTask,
  progressPercent: number,
): void {
  captureEvent('local_upload_reattach_progress', {
    ...localUploadProperties(item),
    progress_percent: progressPercent,
  });
}

export function captureLocalUploadReattachCompleted(
  item: UploadTask,
  elapsedMs: number,
  autoResumed: boolean,
): void {
  captureEvent('local_upload_reattach_completed', {
    ...localUploadProperties(item),
    elapsed_ms: Math.max(0, elapsedMs),
    auto_resumed: autoResumed,
  });
}

export function captureLocalUploadReattachCancelled(
  item: UploadTask,
  elapsedMs: number,
  reason: string,
): void {
  captureEvent('local_upload_reattach_cancelled', {
    ...localUploadProperties(item),
    elapsed_ms: Math.max(0, elapsedMs),
    cancel_reason: reason,
  });
}

export function captureLocalUploadAutoRetryScheduled(
  item: UploadTask,
  event: LocalUploadRetryEvent,
): void {
  captureEvent('local_upload_retry_scheduled', {
    ...localUploadFailureProperties(item, item.phase, event.error),
    operation: event.operation,
    transport: event.transport,
    retry_attempt: event.attempt,
    retry_limit: event.maxRetries,
    retry_delay_ms: event.delayMs,
    waiting_for_network: event.waitingForNetwork,
  });
}

export function captureLocalUploadFailed(
  item: UploadTask,
  stage: UploadFailureStage,
  error: unknown,
): void {
  captureEvent('local_upload_failed', localUploadFailureProperties(item, stage, error));
}

export function captureLocalUploadCompleted(item: UploadTask, resumed: boolean): void {
  captureEvent('local_upload_completed', {
    ...localUploadProperties(item),
    resumed,
    elapsed_ms: item.createdAt ? Math.max(0, Date.now() - item.createdAt) : undefined,
  });
}

export function captureUploadSessionExpiredDetected(item: RestartAnalyticsUpload): void {
  captureEvent('upload_session_expired_detected', restartProperties(item));
}

export function captureUploadRestartClicked(item: UploadTask): void {
  captureEvent('upload_restart_clicked', restartProperties(item));
}

export function captureUploadRestartCreated(
  item: UploadTask,
  result: 'restarted' | 'existing_replacement',
): void {
  captureEvent('upload_restart_created', { ...restartProperties(item), result });
}

export function captureUploadRestartCompletedFromLandedObject(item: UploadTask): void {
  captureEvent('upload_restart_completed_from_landed_object', {
    ...restartProperties(item),
    result: 'completed',
  });
}

export function captureUploadRestartFailed(item: UploadTask, error: unknown): void {
  captureEvent('upload_restart_failed', {
    ...restartProperties(item),
    result: 'failed',
    error_category: restartErrorCategory(error),
  });
}
